import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  InputGroup,
  Text,
} from "@chakra-ui/react";
import useApi from "hooks/useApi";
import { HttpStatusCode } from "axios";
import {
  SORT_TYPES,
  SORT_DIRECTIONS,
} from "constants/filterDropdownData";
import Menu from "components/menu/MainMenu";
import Pagination from "components/pagination/Pagination";
import ErrorDisplay from "components/errorDisplay/ErrorDisplay";
import { SearchBar } from "components/navbar/searchBar/SearchBar";
import { OverlayLoaderComponent } from "components/loader/Loader";
import SidebarFilter from "components/sidebarFilter/SidebarFilter";
import { getFilterFromLocalStorage, setFilterToLocalStorage } from "utils/common";
import { ENTITY_FILTER_TYPES, ENTITY_COLUMNS_TYPES } from "utils/common";
import ManageColumns from "components/manageColumns/ManageColumns";
import NoDataFound from "components/noDataFound/NoDataFound";
import { FiChevronsDown, FiFilter } from "react-icons/fi";
import CustomTable from "components/table/CustomTable";
import { useLocation } from "react-router-dom";
import { ORDER_LISTING_API } from "constants/endpoints";
import { orderColumns } from "./variables";
import { ORDER_STATUS } from "constants/filterDropdownData";
import { formatDateToMMDDYYYY } from "utils/common";
import { SUBSCRIPTION_CREATED_BY } from "constants/filterDropdownData";

const PAGE_SIZE = 50;
const sortableColumns = ["created_at", "amount"];

export default function OrderListing() {
  const [orderData, setOrderData] = useState([]);
  const { apiCall, loading = true, error } = useApi();
  const [isOpenFilterSidebar, setIsOpenFilterSidebar] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");

    const [selectedAction, setSelectedAction] = useState("");
const [selectedRowsData, setSelectedRowsData] = useState([]);
  const lsFilters = getFilterFromLocalStorage(ENTITY_FILTER_TYPES.ORDER_FILTER);
  const initialFiltersData = {
    status: "",
    dateRange: {
      startDate: "",
      endDate: "",
    },
  };

  const [filters, setFilters] = useState(lsFilters ? lsFilters : { ...initialFiltersData });
    const {
    dateRange: {startDate = "", endDate = ""} = {},
  } = filters || {};
  
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    sortType: "created_at",
    sortDirection: "descending",
  });

  const { search } = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(search));

  const fetchOrderData = useCallback(async () => {
    try {
      const body = {
        search_text: searchText,
        page: currentPage,
        limit: pageSize,
        status: filters?.status || "",
        start_date: formatDateToMMDDYYYY(startDate),
        end_date: formatDateToMMDDYYYY(endDate),
        created_by: filters?.created_by?.join(",")
      };
      const res = await apiCall.get(`${ORDER_LISTING_API}`, body);
      if (res?.statusCode === HttpStatusCode.Ok) {
        setOrderData(res?.data?.orders || []);
        setTotalPages(Math.ceil((res?.data?.count || 0) / pageSize));
        setTotalCount(res?.data?.count || 0);
      }
    } catch (error) {
      console.error("Error while fetching orders: ", error.message);
    }
  }, [currentPage, pageSize, searchText, filters]);

  const toggleSidebar = () => {
    setIsOpenFilterSidebar(!isOpenFilterSidebar);
  };

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(1);
  }, []);

  const handleApplyFilter = () => {
    const applyFilterArray = Object.keys(filters).filter((key) => filters[key]);
    setFilteredData(applyFilterArray);
    const filtersWithDateRange = {
      ...filters,
      dateRange: {
        startDate: startDate,
        endDate: endDate,
      },
    };
    setFilterToLocalStorage(ENTITY_FILTER_TYPES.ORDER_FILTER,filtersWithDateRange);
    setCurrentPage(1);
    setSelectedRowsData([]);
    fetchOrderData();
  };

  const handleRemoveAllFilter = () => {
    setFilteredData([]);
    setFilterToLocalStorage(ENTITY_FILTER_TYPES.ORDER_FILTER,{})
    setFilters({
      ...initialFiltersData,
      dateRange: {
        startDate: "",
        endDate: "",
      },
    });
  };

  const dropdownFilters = [
    {
      name: "status",
      placeholder: "Status",
      value: filters?.status || "",
      data: ORDER_STATUS,
    },
    {
      name: 'created_by',
      placeholder: 'Created By',
      value: filters?.created_by,
      data: SUBSCRIPTION_CREATED_BY,
    },
  ];

  const dropdownSorts = [
    {
      name: "sortType",
      placeholder: "",
      value: sortConfig.sortType,
      data: SORT_TYPES,
    },
    {
      name: "sortDirection",
      placeholder: "",
      value: sortConfig.sortDirection,
      data: SORT_DIRECTIONS,
    },
  ];

  const allFiltersProps = {
    dropdowns: dropdownFilters,
    sorts: dropdownSorts,
  };

  const columns = useMemo(() => orderColumns, [orderColumns]);
  const data = useMemo(() => orderData, [orderData]);

  const excludedColumnVisibility = ["user_email"];
  const [columnVisibility, setColumnVisibility] = useState(
    columns.reduce((acc, column) => {
      acc[column.accessor] = !excludedColumnVisibility.includes(column.accessor);
      return acc;
    }, {})
  );

   const [manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox, setManageColumnSelectAllCheckbox] = useState(false);

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  useEffect(() => {
    setFilterToLocalStorage(ENTITY_COLUMNS_TYPES.ORDER_COLUMNS, columnVisibility);
  }, [columnVisibility]);

  useEffect(() => {
    if (queryObject?.search_text) {
      setSearchText(queryObject?.search_text);
    } else {
      setSearchText("");
    }
    fetchOrderData();
  }, [queryObject?.search_text, pageSize, currentPage, sortConfig]);

  const visibleColumns = columns.filter((column) => columnVisibility[column.accessor]);

  const handleInputFormSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOrderData();
  };

    const handleRowSelect = useCallback((row) => {
      const findData = selectedRowsData.find((item) => item._id === row._id);
      setSelectedRowsData((prev) => {
        if (findData) {
          return prev.filter((item) => item._id !== findData._id);
        } else {
          return [...prev, row];
        }
      });
    }, [selectedRowsData]);

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <Box height="100vh" bg="#F8FAFB">
      <Flex
        justify="space-between"
        align="center"
        p="2"
        boxShadow="sm"
        borderRadius="sm"
        pos="fixed"
        style={{ width: "calc(100vw - 96px)" }}
        zIndex={1000}
      >
        <HStack spacing="4" p={2}>
          <Box position="relative" display="inline-block">
            <Button
              paddingX={6}
              leftIcon={<FiFilter />}
              rightIcon={<FiChevronsDown />}
              colorScheme="teal"
              variant="outline"
              borderRadius="md"
              size="sm"
              onClick={toggleSidebar}
            >
              Filter
            </Button>
            {filteredData.length > 0 && (
              <Badge
                position="absolute"
                top="-3"
                right="0"
                colorScheme="teal"
                borderRadius="full"
                px={2}
                fontSize="0.8em"
              >
                {filteredData.length}
              </Badge>
            )}
          </Box>
          <ManageColumns
            columns={columns}
            handleColumnChange={handleColumnChange}
            columnVisibility={columnVisibility}
            setManageColumnSelectAllCheckbox={setManageColumnSelectAllCheckbox}
            manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox}
          />
          <form onSubmit={handleInputFormSubmit}>
            <InputGroup size="sm">
              <SearchBar
                background="#F9F9FB"
                color="#637381"
                setSearchText={(e) => setSearchText(e.target.value)}
                searchText={searchText}
                borderRadius="5px"
                borderWidth="0.5px"
                borderColor="#637381"
              />
            </InputGroup>
          </form>
        </HStack>
         <Text
              borderRadius="5px"
              borderWidth="0.5px"
              borderColor="#637381"
              background="#F9F9FB"
              color="#637381"
              p={1}
              >
              Total Order Listings: {totalCount}
         </Text>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(value) => {
            setSelectedRowsData([]);
            setCurrentPage(value);
          }}
          totalCount={totalCount}
          setCurrentPage={setCurrentPage}
          setPageSize={setPageSize}
          pageSize={pageSize}
          setPageSizeToLocalStorage={(value) =>
            setFilterToLocalStorage(ENTITY_FILTER_TYPES.ORDER_PAGE_SIZE, value)
          }
        />
      </Flex>
      <SidebarFilter
        filters={allFiltersProps}
        handleFilterChange={handleFilterChange}
        handleApplyFilter={handleApplyFilter}
        isOpen={isOpenFilterSidebar}
        onClose={toggleSidebar}
        lsFilters={lsFilters}
        appliedFilters={filters}
        setFilters={setFilters}
        handleRemoveAllFilter={handleRemoveAllFilter}
        isCalenderFilter={true}
        enableMultiselect={true}
        multiSelectKey={["created_by"]}
      />
      {orderData && orderData.length !== 0 ? (
        <CustomTable
          columns={visibleColumns}
          data={data}
          onSelectedAction={setSelectedAction}
          selectedRowsData={selectedRowsData}
          handleRowSelect={handleRowSelect}
          setSelectedRowsData={setSelectedRowsData}
          sortableColumns={sortableColumns}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
            menuComponent={
                        <Menu
                          data={Object.values(ORDER_STATUS)}
                          selectedAction={selectedAction}
                        />
                      }
        />
      ) : !loading ? (
        <NoDataFound />
      ) : null}
      <OverlayLoaderComponent isOpen={loading} />
    </Box>
  );
}
